import { useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";

const STAGING_PASSWORD_KEY = "spotivibly_staging_access";

// Hash is checked client-side for convenience. This isn't military-grade security —
// it just prevents casual visitors from seeing staging content.
// The password is "!Test7878"
const EXPECTED_HASH = "IVRlc3Q3"; // first 8 chars of btoa(password)

function simpleHash(str: string): string {
  return btoa(str).substring(0, 8);
}

function hasAccess(): boolean {
  try {
    return sessionStorage.getItem(STAGING_PASSWORD_KEY) === "granted";
  } catch {
    return false;
  }
}

interface StagingGateProps {
  children: React.ReactNode;
}

export function StagingGate({ children }: StagingGateProps) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [granted, setGranted] = useState(hasAccess);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (simpleHash(password) === EXPECTED_HASH) {
      try {
        sessionStorage.setItem(STAGING_PASSWORD_KEY, "granted");
      } catch (err) {
        console.warn("sessionStorage unavailable:", err);
      }
      setGranted(true);
      setError(false);
    } else {
      setError(true);
    }
  }, [password]);

  if (granted) return <>{children}</>;

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4 p-6">
        <div className="flex items-center justify-center gap-2 text-muted-foreground mb-6">
          <Lock className="h-5 w-5" />
          <span className="text-lg font-medium">Staging Access</span>
        </div>
        <Input
          id="staging-password"
          type="password"
          placeholder="Enter staging password"
          aria-label="Staging password"
          name="staging-gate-code"
          autoComplete="off"
          data-1p-ignore
          data-lpignore="true"
          value={password}
          onChange={(e) => { setPassword(e.target.value); setError(false); }}
        />
        {error && (
          <p role="alert" className="text-sm text-destructive">Incorrect password</p>
        )}
        <Button type="submit" className="w-full">
          Enter Staging
        </Button>
      </form>
    </div>
  );
}
